import {
  Box,
  HStack,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  VStack,
} from '@chakra-ui/react';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import Icon from 'components/Icon';
import Text from 'components/Text';

import MerlinPanel from './MerlinPanel';
import { useDevSandbox } from '../DevSandboxProvider';
import useSandboxTheme from '../useSandboxTheme';

const VIBE_CODING_TIPS = [
  'Be specific — describe layout, colors, and interactions',
  'Start with structure, then iterate on details',
  'Use /review to get feedback on your code',
  'Break complex features into smaller requests',
  'Reference your acceptance criteria or design for better results',
];

interface IMerlinOverlayProps {
  /** When true, mount MerlinPanel invisibly for auto-trigger streaming */
  hidden?: boolean;
}

const MerlinOverlay = ({ hidden }: IMerlinOverlayProps) => {
  const {
    merlin: { isOpen, config, onClose },
    demoSpell,
  } = useDevSandbox();

  const { isFree, isSuperUser } = useCurrentUser();
  const isPaidUser = !isFree || isSuperUser;
  const { colors } = useSandboxTheme();

  // When hidden, only mount if config has autoTrigger
  if (hidden) {
    if (!config?.autoTrigger) return null;
    return (
      <Box display="none">
        <MerlinPanel
          entityName={config.entityName}
          entityType={config.entityType}
          entityDescription={config.entityDescription}
          designScreenshotUrl={config.designScreenshotUrl}
          acceptanceCriteria={config.acceptanceCriteria}
          taskCards={config.taskCards}
          currentCard={config.currentCard}
          cards={config.cards}
          projectScreenName={config.projectScreenName}
          userProjectId={config.userProjectId}
          isDemo={config.isDemo}
          autoTrigger={config.autoTrigger}
          isCustomTemplate={config.isCustomTemplate}
        />
      </Box>
    );
  }

  if (!isOpen || !config) return null;

  return (
    <Box
      bg={colors.shellBg}
      display="flex"
      flexDirection="column"
      width="100%"
      height="100%"
      overflow="hidden"
    >
      {/* Header */}
      <HStack
        px={3}
        py={2}
        borderBottom="1px solid"
        borderColor={colors.shellBorder}
        justifyContent="space-between"
        flexShrink={0}
      >
        <HStack spacing={3}>
          <HStack spacing={1.5}>
            <Icon type="GenerateCodeSolution" size={16} />
            <Text fontSize="small" fontWeight="semibold">
              Merlin AI
            </Text>
          </HStack>
          {isPaidUser && !config?.isDemo && (
            <Popover placement="bottom-start" trigger="click">
              <PopoverTrigger>
                <Box
                  as="button"
                  display="flex"
                  alignItems="center"
                  gap="4px"
                  px={2}
                  py={1}
                  borderRadius="md"
                  opacity={0.5}
                  _hover={{ bg: colors.hoverBg, opacity: 0.8 }}
                  transition="all 0.15s"
                >
                  <Icon type="Tooltip" size={12} />
                  <Text fontSize="micro">Explain</Text>
                </Box>
              </PopoverTrigger>
              <PopoverContent
                bg={colors.popoverBg}
                border="1px solid"
                borderColor={colors.popoverBorder}
                borderRadius="lg"
                maxW="320px"
                boxShadow={colors.popoverShadow}
              >
                <PopoverBody p={3}>
                  <VStack alignItems="flex-start" spacing={2}>
                    <Text fontSize="small" fontWeight="semibold" color="purple.300">
                      Vibe Coding Tips
                    </Text>
                    {VIBE_CODING_TIPS.map((tip, i) => (
                      <HStack key={i} spacing={2} alignItems="flex-start">
                        <Text fontSize="micro" color="purple.400" mt="2px">{i + 1}.</Text>
                        <Text fontSize="micro" opacity="secondary" lineHeight="18px">
                          {tip}
                        </Text>
                      </HStack>
                    ))}
                  </VStack>
                </PopoverBody>
              </PopoverContent>
            </Popover>
          )}
        </HStack>
        <IconButton
          aria-label="Close Merlin"
          icon={<Icon type="Close" size={16} />}
          variant="ghost"
          size="sm"
          minW="30px"
          h="30px"
          borderRadius="md"
          onClick={() => {
            if (config?.isDemo && demoSpell.state.isSpellActive) {
              demoSpell.revert();
            }
            onClose();
          }}
          _hover={{ bg: colors.hoverBg }}
        />
      </HStack>

      {/* MerlinPanel content */}
      <Box flex={1} overflow="hidden">
        <MerlinPanel
          entityName={config.entityName}
          entityType={config.entityType}
          entityDescription={config.entityDescription}
          designScreenshotUrl={config.designScreenshotUrl}
          acceptanceCriteria={config.acceptanceCriteria}
          taskCards={config.taskCards}
          currentCard={config.currentCard}
          cards={config.cards}
          projectScreenName={config.projectScreenName}
          userProjectId={config.userProjectId}
          isDemo={config.isDemo}
          autoTrigger={config.autoTrigger}
          isCustomTemplate={config.isCustomTemplate}
        />
      </Box>
    </Box>
  );
};

export default MerlinOverlay;
